import { useEffect } from "react";
import { toast } from "sonner";
import { Bell, Clock, X } from "lucide-react";
import api from "@/lib/api";

/**
 * LiveToaster (Iter34, snooze in Iter39)
 * Subscribes to the global `kobolog:live` window event and pops a sonner
 * toast the instant a reminder fires on WhatsApp/Email. The toast includes
 * three snooze buttons (10 / 30 / 60 min) — tapping any of them POSTs to
 * /api/reminders/{id}/snooze which resets the reminder to `pending` and
 * bumps `due_at`. The change flows through the Unified Brain sync
 * (last-write-wins on `updated_at`) so a phone offline copy loses to this
 * newer write when it later reconnects.
 *
 * Also handles `reminder_snoozed` events (fired by the same backend) so
 * open tabs never show a stale toast for a reminder already snoozed from
 * another surface.
 */
const SNOOZE_OPTIONS = [10, 30, 60];

export default function LiveToaster() {
  useEffect(() => {
    const handler = (e) => {
      const kind = e?.detail?.kind;
      const meta = e?.detail?.meta || {};
      if (kind !== "reminder_fired") return;
      const task = (meta.task || "Reminder due").slice(0, 200);
      const via = meta.delivered_via ? ` via ${meta.delivered_via}` : "";
      const reminderId = meta.reminder_id;

      toast.custom(
        (id) => (
          <ReminderToast
            toastId={id}
            task={task}
            via={via}
            reminderId={reminderId}
          />
        ),
        { duration: 20_000 }
      );
    };
    window.addEventListener("kobolog:live", handler);
    return () => window.removeEventListener("kobolog:live", handler);
  }, []);
  return null;
}

function ReminderToast({ toastId, task, via, reminderId }) {
  const snooze = async (mins) => {
    if (!reminderId) {
      toast.error("This reminder cannot be snoozed (missing id).");
      return;
    }
    try {
      await api.post(`/reminders/${reminderId}/snooze`, { minutes: mins });
      toast.dismiss(toastId);
      toast.success(`Snoozed for ${mins} min`, { duration: 3000 });
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || `Couldn't snooze the reminder`
      );
    }
  };

  return (
    <div
      className="bg-white border border-border shadow-2xl rounded-2xl p-4 w-[92vw] sm:w-[380px]"
      data-testid="reminder-toast"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 shrink-0 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
          <Bell className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-kudi-ink/55 font-bold">
            Reminder fired{via}
          </div>
          <div className="text-sm font-medium mt-0.5 line-clamp-3" data-testid="reminder-toast-task">
            {task}
          </div>
        </div>
        <button
          onClick={() => toast.dismiss(toastId)}
          className="w-7 h-7 rounded-full hover:bg-accent/40 flex items-center justify-center"
          aria-label="Dismiss"
          data-testid="reminder-toast-close"
        >
          <X className="w-3.5 h-3.5 text-kudi-ink/60" />
        </button>
      </div>
      {reminderId && (
        <div className="mt-3 flex items-center gap-2 pl-12">
          <span className="text-[10px] uppercase tracking-wider text-kudi-ink/45 font-semibold flex items-center gap-1">
            <Clock className="w-3 h-3" /> Snooze
          </span>
          {SNOOZE_OPTIONS.map((m) => (
            <button
              key={m}
              onClick={() => snooze(m)}
              className="text-xs font-semibold px-2.5 py-1.5 rounded-full bg-kudi-green/10 text-kudi-green hover:bg-kudi-green hover:text-white transition"
              data-testid={`reminder-toast-snooze-${m}`}
            >
              {m} min
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
